import React, { useRef, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

export default function VoiceLogs() {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [transcripts, setTranscripts] = useState([]);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const b64 = await blobToBase64(blob);
        setBusy(true);
        try {
          const { data } = await apiClient().post("/ai/transcribe", { audio_base64: b64 });
          setTranscripts((arr) => [{ id: data.id, text: data.text, at: new Date().toISOString() }, ...arr]);
          toast.success("Transcribed");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Transcription failed");
        } finally { setBusy(false); }
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission required");
    }
  };
  const stop = () => { mediaRef.current?.stop(); setRecording(false); };

  return (
    <div className="space-y-6 fade-in-up" data-testid="voice-page">
      <div>
        <h1 className="font-display text-5xl mt-1">Voice Site Logs</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Speak your update. We transcribe it and attach to today&apos;s log.</p>
      </div>

      <div className="border border-border bg-card p-8 grid place-items-center">
        {!recording ? (
          <button onClick={start} disabled={busy} data-testid="voice-start-btn"
            className={`btn-sharp inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-4 font-medium ${busy ? "opacity-50" : "pulse-ring"}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-5 w-5" />}
            {busy ? "Transcribing…" : "Start recording"}
          </button>
        ) : (
          <button onClick={stop} data-testid="voice-stop-btn"
            className="btn-sharp inline-flex items-center gap-2 bg-destructive text-destructive-foreground px-6 py-4 font-medium">
            <Square className="h-5 w-5" /> Stop
          </button>
        )}
      </div>

      <div className="space-y-2">
        {transcripts.length === 0 && <div className="text-sm text-muted-foreground">No transcripts yet.</div>}
        {transcripts.map((t) => (
          <div key={t.id} className="border border-border bg-card p-4" data-testid={`voice-item-${t.id}`}>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(t.at).toLocaleString()}</div>
            <div className="mt-1 text-sm leading-relaxed">{t.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
